import { create } from 'zustand'
import type { Team, TeamWithMembers, TeamFilterOptions } from '@/types'
import { useAuthStore } from './auth'
import { api } from '@/lib/api'
import { normalizeTeamIds } from '@/lib/utils/teams'

interface TeamsState {
  // State
  teams: Team[]
  currentTeam: TeamWithMembers | null
  isLoading: boolean
  error: string | null

  // Team filtering for agent dashboard
  teamFilter: TeamFilterOptions
}

interface TeamsActions {
  // Actions
  fetchTeams: (siteId?: string) => Promise<void>
  fetchTeamById: (id: number) => Promise<TeamWithMembers | null>
  setTeamFilter: (filter: Partial<TeamFilterOptions>) => void
  clearError: () => void
  clearTeamState: () => void
  assignUserToTeam: (userId: number, teamIds: number[]) => Promise<boolean>
  assignBotToTeam: (botId: number, teamId: number | null) => Promise<boolean>

  // Helper methods
  getTeamById: (id: number) => Team | null
  getTeamName: (id: number | null | undefined) => string | null
}

type TeamsStore = TeamsState & TeamsActions

export const useTeamsStore = create<TeamsStore>()((set, get) => ({
  // Initial state
  teams: [],
  currentTeam: null,
  isLoading: false,
  error: null,
  teamFilter: {
    showAllTeams: true,
    selectedTeamId: null,
    availableTeams: [],
  },

  // Actions
  fetchTeams: async () => {
    set({ isLoading: true, error: null })
    try {
      // Check if we're in agent context by reading auth state directly
      // from the zustand store. Do NOT call React hooks inside store
      // actions (they must only be used in function components or
      // custom hooks).
      const { isAgentAuthenticated } = useAuthStore.getState()
      let data

      if (isAgentAuthenticated) {
        // Use agent endpoint for agent dashboard
        const response = await api.agent.getTeams()
        if (!response?.success) {
          throw new Error(response?.error || 'Failed to fetch teams')
        }
        data = response.data || []
      } else {
        // Use admin endpoint for admin dashboard
        const response = await api.admin.listTeams()
        if (!response?.success) {
          throw new Error(response?.error || 'Failed to fetch teams')
        }
        data = response.data || []
      }

      const teams = Array.isArray(data) ? data : []

      set({
        teams,
        teamFilter: {
          ...get().teamFilter,
          availableTeams: teams.filter((team: Team) => team.isActive),
        },
        isLoading: false,
      })
    } catch (error) {
      console.error('Error fetching teams:', error)
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch teams',
        isLoading: false,
      })
    }
  },

  fetchTeamById: async (id: number) => {
    set({ isLoading: true, error: null })
    try {
      const resp = await api.admin.getTeamById(id)
      const teamPayload: any = (resp && (resp as any).data) ?? resp
      const team = teamPayload as TeamWithMembers
      set({ currentTeam: team, isLoading: false })
      return team
    } catch (error) {
      console.error('Error fetching team:', error)
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch team',
        isLoading: false,
      })
      return null
    }
  },

  setTeamFilter: (filter: Partial<TeamFilterOptions>) => {
    set({
      teamFilter: {
        ...get().teamFilter,
        ...filter,
      },
    })
  },

  clearError: () => {
    set({ error: null })
  },

  clearTeamState: () => {
    set({
      teams: [],
      currentTeam: null,
      isLoading: false,
      error: null,
      teamFilter: {
        showAllTeams: true,
        selectedTeamId: null,
        availableTeams: [],
      },
    })
  },

  // Helper methods
  getTeamById: (id: number) => {
    const { teams } = get()
    return teams.find(team => team.id === id) || null
  },

  getTeamName: (id: number | null | undefined) => {
    if (!id) return null
    const team = get().getTeamById(id)
    return team?.name || null
  },

  // Team assignment operations
  assignUserToTeam: async (userId: number, teamIds: number[]) => {
    try {
      const normalizedTeamIds = normalizeTeamIds(teamIds)

      const response = await api.admin.setUserTeams(userId, normalizedTeamIds)
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to update user teams')
      }

      return true
    } catch (error) {
      console.error('Error in team assignment:', error)
      throw error
    }
  },

  assignBotToTeam: async (botId: number, teamId: number | null) => {
    try {
      if (teamId === null) {
        // Remove bot from team
        const response = await api.admin.removeBotFromTeam(botId)
        if (!response?.success) {
          throw new Error(response?.error || 'Failed to remove bot from team')
        }
      } else {
        // Assign bot to team
        const response = await api.admin.assignBotToTeam(botId, teamId)
        if (!response?.success) {
          throw new Error(response?.error || 'Failed to assign bot to team')
        }
      }

      return true
    } catch (error) {
      console.error('Error in bot team assignment:', error)
      throw error
    }
  },
}))
